/**
 * FRONT-17: Composant de selection des lots techniques et zones d'intervention
 * Adapte pour l'inscription prestataire (pas de sauvegarde API immediate)
 * 
 * Base sur LotsSelectorV2 avec les differences suivantes :
 * - Pas d'appels API a chaque changement
 * - Stockage en state local via callback onChange
 * - Pas de gestion des lots AC (pas d'AC a l'inscription)
 * - Tags de lots plus gros et cliquables avec distinction visuelle
 * 
 * Comportement :
 * - Dropdown selectionne UN lot -> affiche ses zones
 * - Tags cliquables pour les lots deja selectionnes
 * - Lot 1xx -> Zones regionales (checkbox regions)
 * - Lot 2xx -> Zones departementales (checkbox departements)
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import '../../Profil/components/LotsSelector.css';

// Composant checkbox avec état indéterminé — déclaré hors du composant parent
// pour éviter un remount (et donc un flash) à chaque re-render du parent
const IndeterminateCheckbox: React.FC<{
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  disabled?: boolean;
}> = ({ checked, indeterminate, onChange, disabled }) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
  );
};

// Types
interface LotTechnique {
  id: number;
  nom: string;
  code?: string;
}

interface Region {
  id: number;
  nom: string;
}

interface Departement {
  id: number;
  nom: string;
  code?: string;
  id_region?: number;
  region?: Region;
}

export interface LotDepartementSelection {
  lot_technique_id: number;
  lot_technique_code?: string;
  lot_technique_nom?: string;
  departement_id: number;
  departement_nom?: string;
  region_id?: number;
  region_nom?: string;
}

interface LotsSelectorInscriptionProps {
  /** Lots départements sélectionnés (state parent) */
  selectedLots: LotDepartementSelection[];
  /** Callback pour mettre à jour le state parent */
  onChange: (lots: LotDepartementSelection[]) => void;
  /** Désactivé */
  disabled?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL;

const LotsSelectorInscription: React.FC<LotsSelectorInscriptionProps> = ({
  selectedLots,
  onChange,
  disabled = false,
}) => {
  // États
  const [lotsTechniques, setLotsTechniques] = useState<LotTechnique[]>([]);
  const [departements, setDepartements] = useState<Departement[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lot actuellement sélectionné dans la dropdown (pour afficher ses zones)
  const [currentLotId, setCurrentLotId] = useState<number | null>(null);

  // Charger les données de référence
  useEffect(() => {
    const fetchReferentiel = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Charger les lots techniques
        const lotsResponse = await fetch(`${API_URL}/lots-technique/categories`, {
          headers: { 'Accept': 'application/json' }
        });
        const lotsResult = await lotsResponse.json();
        const lotsData = Array.isArray(lotsResult) ? lotsResult :
          (lotsResult.data && Array.isArray(lotsResult.data)) ? lotsResult.data : [];
        setLotsTechniques(lotsData);

        // Charger les départements avec leurs régions
        const deptsResponse = await fetch(`${API_URL}/departements`, {
          headers: { 'Accept': 'application/json' }
        });
        const deptsResult = await deptsResponse.json();
        const deptsData: Departement[] = deptsResult.data || [];
        setDepartements(deptsData);

        // Extraire les régions uniques depuis les départements
        const regionsMap = new Map<number, Region>();
        deptsData.forEach((dept: Departement) => {
          if (dept.region && dept.region.id && !regionsMap.has(dept.region.id)) {
            regionsMap.set(dept.region.id, dept.region);
          }
        });
        setRegions(Array.from(regionsMap.values()).sort((a, b) => a.nom.localeCompare(b.nom)));

      } catch (err) {
        setError('Erreur lors du chargement des données');
        console.error('LotsSelectorInscription fetchReferentiel error:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReferentiel();
  }, []);

  // Lots techniques déjà sélectionnés (extraits des selectedLots)
  const selectedLotsTechniques = useMemo(() => {
    const lotsSet = new Set<number>();
    selectedLots.forEach(ld => {
      if (ld.lot_technique_id) lotsSet.add(ld.lot_technique_id);
    });
    return Array.from(lotsSet);
  }, [selectedLots]);

  // Départements sélectionnés pour le lot courant
  const currentLotDepartements = useMemo(() => {
    if (!currentLotId) return [];
    return selectedLots
      .filter(ld => ld.lot_technique_id === currentLotId)
      .map(ld => ld.departement_id);
  }, [selectedLots, currentLotId]);

  // Infos du lot courant
  const currentLot = useMemo(() => {
    return lotsTechniques.find(l => l.id === currentLotId);
  }, [lotsTechniques, currentLotId]);

  // Déterminer si un lot est régional (1xx) ou départemental (2xx)
  const isLotRegional = useCallback((lotId: number): boolean => {
    const lot = lotsTechniques.find(l => l.id === lotId);
    if (!lot) return false;
    // Utiliser le code si disponible, sinon l'id
    const code = lot.code ? parseInt(lot.code) : lotId;
    return code >= 100 && code < 200;
  }, [lotsTechniques]);

  // Départements par région
  const departementsByRegion = useMemo(() => {
    const map = new Map<number, number[]>();
    departements.forEach(dept => {
      const regionId = dept.id_region || dept.region?.id;
      if (regionId) {
        if (!map.has(regionId)) map.set(regionId, []);
        map.get(regionId)!.push(dept.id);
      }
    });
    return map;
  }, [departements]);

  // Calculer l'état d'une région pour le lot courant
  const getRegionState = useCallback((regionId: number): 'checked' | 'unchecked' | 'indeterminate' => {
    const regionDepts = departementsByRegion.get(regionId) || [];
    if (regionDepts.length === 0) return 'unchecked';

    const selectedInRegion = regionDepts.filter(deptId => currentLotDepartements.includes(deptId));

    if (selectedInRegion.length === 0) return 'unchecked';
    if (selectedInRegion.length === regionDepts.length) return 'checked';
    return 'indeterminate';
  }, [departementsByRegion, currentLotDepartements]);

  // Sélectionner un lot dans la dropdown
  const handleLotSelect = (lotId: number) => {
    setCurrentLotId(lotId);
  };

  // Helper: Mettre à jour les sélections pour un lot donné
  const updateLotsForLotTechnique = (lotTechniqueId: number, newDeptIds: number[]) => {
    const lot = lotsTechniques.find(l => l.id === lotTechniqueId);
    if (!lot) return;

    // Retirer les anciennes sélections pour ce lot
    const otherLots = selectedLots.filter(ld => ld.lot_technique_id !== lotTechniqueId);

    // Ajouter les nouvelles sélections
    const newSelections: LotDepartementSelection[] = newDeptIds.map(deptId => {
      const dept = departements.find(d => d.id === deptId);
      return {
        lot_technique_id: lotTechniqueId,
        lot_technique_code: lot.code,
        lot_technique_nom: lot.nom,
        departement_id: deptId,
        departement_nom: dept?.nom,
        region_id: dept?.id_region || dept?.region?.id,
        region_nom: dept?.region?.nom,
      };
    });

    onChange([...otherLots, ...newSelections]);
  };

  // Supprimer un lot technique (et toutes ses zones)
  const handleRemoveLot = (lotTechniqueId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newLots = selectedLots.filter(ld => ld.lot_technique_id !== lotTechniqueId);
    onChange(newLots);
    // Si le lot supprimé était le lot courant, réinitialiser
    if (currentLotId === lotTechniqueId) {
      setCurrentLotId(null);
    }
  };

  // Cocher/décocher une région (lot 1xx)
  const handleRegionChange = (regionId: number) => {
    if (disabled || !currentLotId) return;

    const regionDepts = departementsByRegion.get(regionId) || [];
    const currentState = getRegionState(regionId);

    let newDepartements: number[];

    if (currentState === 'checked' || currentState === 'indeterminate') {
      // Désélectionner : retirer les départements de cette région
      newDepartements = currentLotDepartements.filter(deptId =>
        !regionDepts.includes(deptId)
      );
    } else {
      // Sélectionner : ajouter les départements de cette région
      newDepartements = Array.from(new Set([...currentLotDepartements, ...regionDepts]));
    }

    updateLotsForLotTechnique(currentLotId, newDepartements);
  };

  // Cocher/décocher un département (lot 2xx)
  const handleDepartementChange = (deptId: number) => {
    if (disabled || !currentLotId) return;

    const isSelected = currentLotDepartements.includes(deptId);
    const newDepartements = isSelected
      ? currentLotDepartements.filter(id => id !== deptId)
      : [...currentLotDepartements, deptId];

    updateLotsForLotTechnique(currentLotId, newDepartements);
  };

  // Tout sélectionner/désélectionner
  const handleSelectAll = () => {
    if (disabled || !currentLotId) return;

    const allDepts = departements.map(d => d.id);
    const allSelected = allDepts.every(deptId => currentLotDepartements.includes(deptId));

    const newDepartements = allSelected ? [] : allDepts;
    updateLotsForLotTechnique(currentLotId, newDepartements);
  };

  // Composant checkbox inline supprimé - voir déclaration module-level ci-dessus

  if (isLoading) {
    return (
      <div className="lots-selector loading">
        <div className="loading-spinner"></div>
        <p>Chargement des lots...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lots-selector error">
        <p>{error}</p>
      </div>
    );
  }

  const isCurrentLotRegional = currentLotId ? isLotRegional(currentLotId) : false;

  return (
    <div className={`lots-selector ${disabled ? 'disabled' : ''}`}>
      {/* Section 1: Sélection du lot technique */}
      <section className="selector-section">
        <div className="selector-header">
          <h3>Lots techniques</h3>
          <p className="selector-info">Sélectionnez les lots où vous souhaitez être référencé</p>
        </div>

        <div className="lots-selection">
          {/* Dropdown de sélection */}
          <select
            value={currentLotId || ''}
            onChange={e => {
              const lotId = Number(e.target.value);
              if (lotId) handleLotSelect(lotId);
            }}
            disabled={disabled}
            className="lot-select"
          >
            <option value="">Sélectionnez un lot technique...</option>
            {lotsTechniques.map(lot => (
              <option key={lot.id} value={lot.id}>
                {lot.code ? `${lot.code} - ` : ''}{lot.nom}
                {isLotRegional(lot.id) ? ' (régional)' : ' (départemental)'}
              </option>
            ))}
          </select>

          {/* Tags des lots deja selectionnes */}
          {selectedLotsTechniques.length > 0 && (
            <div className="selected-tags inscription-tags">
              {selectedLotsTechniques.map(lotId => {
                const lot = lotsTechniques.find(l => l.id === lotId);
                const isActive = lotId === currentLotId;
                const deptCount = selectedLots.filter(ld => ld.lot_technique_id === lotId).length;

                return lot ? (
                  <span
                    key={lotId}
                    className={`lot-tag lot-tag-clickable ${isActive ? 'lot-tag-active' : ''}`}
                    onClick={() => handleLotSelect(lotId)}
                    title={`Cliquez pour voir/modifier les zones (${deptCount} zone${deptCount > 1 ? 's' : ''})`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLotSelect(lotId)}
                  >
                    {lot.code ? `${lot.code} - ` : ''}{lot.nom}
                    <span className="zone-count">({deptCount})</span>
                    <button
                      type="button"
                      className="remove-tag"
                      onClick={(e) => handleRemoveLot(lotId, e)}
                      title="Retirer ce lot"
                    >
                      x
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
      </section>

      {/* Section 2: Zones régionales (lot 1xx) */}
      {currentLotId && isCurrentLotRegional && (
        <section className="selector-section zones-section">
          <div className="selector-header">
            <h3>Zones régionales (pour lot {currentLot?.code || currentLotId})</h3>
            <p className="selector-info">
              Sélectionner une région = intervention sur tous ses départements
            </p>
          </div>

          <div className="selector-toolbar">
            <button
              type="button"
              className="select-all-btn"
              onClick={handleSelectAll}
              disabled={disabled}
            >
              {departements.every(d => currentLotDepartements.includes(d.id))
                ? 'Tout désélectionner'
                : 'Tout sélectionner'}
            </button>
          </div>

          <div className="regions-grid">
            {regions.map(region => {
              const state = getRegionState(region.id);

              return (
                <label
                  key={region.id}
                  className={`region-checkbox ${state === 'checked' ? 'selected' : ''} ${state === 'indeterminate' ? 'partial' : ''}`}
                >
                  <IndeterminateCheckbox
                    checked={state === 'checked'}
                    indeterminate={state === 'indeterminate'}
                    onChange={() => handleRegionChange(region.id)}
                    disabled={disabled}
                  />
                  <span className="region-nom">{region.nom}</span>
                </label>
              );
            })}
          </div>

          <div className="storage-note">
            <span>Les départements de chaque région sélectionnée seront enregistrés</span>
          </div>
        </section>
      )}

      {/* Section 3: Zones départementales (lot 2xx) */}
      {currentLotId && !isCurrentLotRegional && (
        <section className="selector-section zones-section">
          <div className="selector-header">
            <h3>Zones départementales (pour lot {currentLot?.code || currentLotId})</h3>
            <p className="selector-info">
              Sélection département par département
            </p>
          </div>

          <div className="selector-toolbar">
            <button
              type="button"
              className="select-all-btn"
              onClick={handleSelectAll}
              disabled={disabled}
            >
              {departements.every(d => currentLotDepartements.includes(d.id))
                ? 'Tout désélectionner'
                : 'Tout sélectionner'}
            </button>
          </div>

          <div className="departements-grid">
            {departements.map(dept => {
              const isSelected = currentLotDepartements.includes(dept.id);

              return (
                <label
                  key={dept.id}
                  className={`departement-checkbox ${isSelected ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleDepartementChange(dept.id)}
                    disabled={disabled}
                  />
                  <span className="dept-code">{String(dept.id).padStart(2, '0')}</span>
                  <span className="dept-separator">-</span>
                  <span className="dept-nom">{dept.nom}</span>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {/* Message si aucun lot sélectionné */}
      {!currentLotId && selectedLotsTechniques.length === 0 && (
        <div className="no-lots-notice">
          <p>Sélectionnez un lot technique pour définir vos zones d'intervention</p>
        </div>
      )}

      {/* Message si lots selectionnes mais aucun affiche */}
      {!currentLotId && selectedLotsTechniques.length > 0 && (
        <div className="select-lot-notice">
          <p>Cliquez sur un lot ci-dessus pour voir ou modifier ses zones d'intervention</p>
        </div>
      )}

      {/* Résumé de la sélection */}
      {selectedLots.length > 0 && (
        <div className="selection-summary">
          <strong>{selectedLotsTechniques.length}</strong> lot{selectedLotsTechniques.length > 1 ? 's' : ''} technique{selectedLotsTechniques.length > 1 ? 's' : ''},
          <strong> {selectedLots.length}</strong> zone{selectedLots.length > 1 ? 's' : ''} sélectionnée{selectedLots.length > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default LotsSelectorInscription;
